"use client";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { Link } from "@/components/CustomLink";
import { buttonVariants } from "@/components/ui/button";
import { ROUTES } from "@/@core/constants/routes";
import { useViewMode } from "@/context/ViewModeContext";
import { useSite } from "@/lib/hooks/site";
import { cn } from "@/lib/utils";
import { isExternalUrl, sendCampaignEvent } from "@/lib/site-campaign/client";
import { isPathTargeted } from "@/lib/site-campaign/targeting";
import { useSiteCampaign } from "./SiteCampaignProvider";

/**
 * Footer/alt navigasyonun hemen üstünde, normal sayfa akışında duran
 * kampanya butonu (SAB-361). Drawer'dan bağımsızdır; sunucuda da
 * render edildiği için arama motorları ve ziyaretçi aynı bağlantıyı görür.
 */
export default function SiteCampaignButton() {
  const { config, preview } = useSiteCampaign();
  const pathname = usePathname();
  const site = useSite();
  const { viewMode } = useViewMode();
  const ref = useRef<HTMLAnchorElement>(null);

  const button = config?.button;
  const visible =
    Boolean(button) &&
    viewMode !== "map" &&
    pathname !== ROUTES.CONTACT_WITH_VENUES &&
    isPathTargeted(pathname, button?.targeting, site?.setting?.routes);

  // Görüntülenme: buton ekrana girdiğinde, sayfa başına bir kez.
  useEffect(() => {
    const element = ref.current;
    if (!visible || preview || !element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          sendCampaignEvent({ module: "button", event: "impression" });
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [visible, preview, pathname]);

  if (!visible || !button) return null;

  const onClick = () => {
    if (!preview) sendCampaignEvent({ module: "button", event: "click" });
  };
  const external = isExternalUrl(button.targetUrl);
  const linkProps = button.openInNewTab
    ? { target: "_blank", rel: "noopener noreferrer" }
    : external
      ? { rel: "noopener" }
      : {};
  const className = cn(buttonVariants({ variant: button.variant, size: "lg" }), "w-full max-w-md text-center");

  return (
    <div className="container mt-6 flex justify-center md:mt-8">
      {external ? (
        <a ref={ref} href={button.targetUrl} onClick={onClick} className={className} {...linkProps}>
          {button.text}
        </a>
      ) : (
        <Link ref={ref} href={button.targetUrl} onClick={onClick} className={className} {...linkProps}>
          {button.text}
        </Link>
      )}
    </div>
  );
}
